import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useFlexLayout, useSortBy, useTable, useRowSelect, useResizeColumns, useColumnOrder } from 'react-table';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { TableContainer, TableRow as MuiTableRow } from '@material-ui/core';
import HeaderCell from './HeaderCell';
import { IndeterminateCheckbox } from '@/components/DispatchUnassigned/table';
import { useSticky } from 'react-table-sticky';
import { sansSerif } from '@/styles/typography';
import { colors } from '@/styles';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable as Droppable } from '../DispatchPlan/stops/StrictModeDroppable';
import HiddenColumnsRestoreHeader from '@/components/CustomizableTable/HiddenColumnsRestoreHeader';

const RESTORE_COLUMN_ID = 'show_hidden_columns';
const NON_HIDEABLE_COLUMNS = new Set(['selection', RESTORE_COLUMN_ID]);

const SELECTION_CHECKBOX_SCALE = 1.2;
const VIRTUAL_ROW_HEIGHT = 40;
const VIRTUAL_OVERSCAN = 12;

const ScaledSelectionCheckbox = (props) => (
    <span
        css={css`
            display: inline-flex;
            transform: scale(${SELECTION_CHECKBOX_SCALE});
            transform-origin: center;
        `}
    >
        <IndeterminateCheckbox {...props} />
    </span>
);

const ControlledSelectionCell = ({ controller, rowId }) => {
    const selected = useSyncExternalStore(controller.subscribe, () => controller.isRowSelected(rowId));
    const disabled = controller.isRowDisabled ? controller.isRowDisabled(rowId) : false;
    return <ScaledSelectionCheckbox checked={selected} onChange={() => controller.toggleRow(rowId)} disabled={disabled} />;
};

const ControlledSelectionHeader = ({ controller }) => {
    const headerState = useSyncExternalStore(controller.subscribe, () => controller.getHeaderState());
    return (
        <ScaledSelectionCheckbox
            checked={headerState === 'all'}
            indeterminate={headerState === 'some'}
            onChange={() => controller.toggleAll()}
        />
    );
};

// Shared config for the restore column (sticky-right, fixed width, non-sortable/non-resizable)
const createRestoreColumnConfig = (RestoreColumnHeader) => ({
    id: RESTORE_COLUMN_ID,
    Header: RestoreColumnHeader,
    Cell: () => null,
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    disableResizing: true,
    disableSortBy: true,
    canHide: false,
    sticky: 'right',
});

const Body = styled.span`
    ${sansSerif}
    font-size: 14px;
    font-weight: 500;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Cell = styled.div`
    display: flex;
    align-items: center;
    padding: 6px 24px 6px 16px;
    border-bottom: 1px solid #e0e0e0;
    background-color: ${(props) => (props.color ? props.color : colors.white.primary)};
    transition: background-color 0.2s ease;
    min-width: 0;

    &[data-sticky-td] {
        background: ${(props) => (props.color ? props.color : colors.white.primary)};
        position: sticky;
        display: flex;
    }

    &[data-sticky-last-left-td] {
        border-left: 2px 0px 3px #ccc;
    }

    &[data-sticky-first-right-td] {
        box-shadow: -2px 0px 3px #ccc;
    }
`;

const TableRow = styled((props) => {
    const { useDiv, ...rest } = props;
    return useDiv ? <div {...rest} /> : <MuiTableRow {...rest} />;
})`
    display: flex;
    cursor: pointer;
    color: ${(props) => (props.error ? '#f44336' : '#2b2b2b')};

    &:hover {
        & > div {
            background: ${(props) => (props.color ? props.color : '#d1d1d1')} !important;
        }
    }
`;

const BodyCell = ({ cell, index, color, renderContext = {}, wrapContent = true, ...props }) => {
    const isSelection = cell.column.id === 'selection';
    const isRestore = cell.column.id === RESTORE_COLUMN_ID;
    const renderBare = isSelection || isRestore || !wrapContent;

    return (
        <Cell
            {...props}
            index={index}
            color={color}
            css={
                (isSelection || isRestore) &&
                css`
                    justify-content: center;
                    padding-left: 0;
                    padding-right: 0;
                    padding-top: 0;
                    padding-bottom: 0;
                `
            }
        >
            {renderBare ? cell.render('Cell', renderContext) : <Body>{cell.render('Cell', renderContext)}</Body>}
        </Cell>
    );
};

/**
 * Consolidated CustomizableTable component for rendering sortable customizable data tables
 *
 * @param {Array} data - Array of row objects to display
 * @param {Array} orders - Deprecated alias for data (backwards compatibility)
 * @param {Array} columns - Column definitions for react-table
 * @param {Object} tab - Current tab object with value property
 * @param {Function} onRowClick - Callback when a row is clicked (row, event?) => void. Event is passed for ctrl+click etc.
 * @param {Boolean} enableRowSelection - Whether to show selection checkboxes
 * @param {Object} selectedRowIds - Object mapping row IDs to selection state
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {Object} sortBy - Current sort state { id, desc }
 * @param {Function} onSortChange - Callback when sort changes
 * @param {Function} highlightRow - Function to add highlighting logic to rows
 * @param {Object} callbacks - Legacy callback bag (used by existing order pages)
 * @param {Object|Function} cellRenderContext - Extra context passed to each cell render
 * @param {Function} getRowId - Optional row id resolver for non-order datasets
 * @param {Boolean} manualSortBy - Whether sorting is controlled externally (default: true)
 * @param {Object} milestoneThresholdSettings - Settings for milestone thresholds (MyOrders)
 * @param {String} view - View type (MyOrders)
 * @param {Boolean} useDiv - Use div instead of MuiTableRow for the row wrapper
 * @param {Boolean} enableColumnResizing - Whether to enable column resizing (default: true)
 * @param {Object} defaultColumnConfig - Default column configuration (minWidth, width, maxWidth)
 * @param {Boolean} enableColumnReordering - Whether to enable drag-and-drop column reordering (default: true)
 * @param {Function} onLoadMore - Callback to load more rows when scrolled near bottom (infinite scroll)
 * @param {Boolean} hasMore - Whether more rows are available to load
 * @param {Boolean} isLoading - Whether a load is currently in progress
 */
const CustomizableTable = ({
    data,
    orders,
    columns,
    tab,
    onRowClick,
    enableRowSelection = false,
    selectedRowIds = {},
    onSelectionChange,
    selectionController,
    sortBy = [],
    onSortChange,
    highlightRow,
    callbacks = {},
    milestoneThresholdSettings,
    view,
    useDiv = true,
    enableColumnResizing = true,
    defaultColumnConfig,
    enableColumnReordering = true,
    onLoadMore,
    loadMoreThreshold = 200,
    hasMore = false,
    isLoading = false,
    virtualizeRows = false,
    filterableColumns = [],
    onOpenColumnFilter,
    onClearColumnFilter,
    initialState: initialStateProp,
    onLayoutChange,
    cellRenderContext,
    getRowId,
    wrapBodyCellContent = true,
    manualSortBy = true,
}) => {
    const tableData = useMemo(() => data || orders || [], [data, orders]);

    const selectionControllerRef = useRef(selectionController);
    selectionControllerRef.current = selectionController;

    const [hiddenColumns, setHiddenColumnsState] = useState(() => initialStateProp?.hiddenColumns || []);
    const defaultColumn = useMemo(
        () =>
            defaultColumnConfig || {
                minWidth: 50,
                width: 150,
                maxWidth: 500,
            },
        [defaultColumnConfig]
    );
    const filterableColumnSet = useMemo(() => new Set(filterableColumns || []), [filterableColumns]);
    const columnLabels = useMemo(() => {
        const labels = {};
        const extractLabels = (col) => {
            if (col?.columns?.length) {
                col.columns.forEach(extractLabels);
            } else {
                const id = col?.id || col?.accessor;
                if (id) {
                    const headerVal =
                        (typeof col.Header === 'string' && col.Header) ||
                        (typeof col.header === 'string' && col.header) ||
                        (typeof col.label === 'string' && col.label) ||
                        (typeof col.title === 'string' && col.title);
                    labels[id] = headerVal || id.replace(/_/g, ' ');
                }
            }
        };

        (columns || []).forEach(extractLabels);
        labels[RESTORE_COLUMN_ID] = labels[RESTORE_COLUMN_ID] || 'Hidden Columns';
        return labels;
    }, [columns]);

    const isColumnFilterable = useCallback((columnId) => filterableColumnSet.has(columnId), [filterableColumnSet]);

    const hiddenColumnEntries = useMemo(
        () =>
            hiddenColumns
                .filter((id) => !NON_HIDEABLE_COLUMNS.has(id))
                .map((id) => ({ id, label: columnLabels[id] || id })),
        [hiddenColumns, columnLabels]
    );

    // Keep a ref so the restore-column header component can stay stable and not
    // cause the columns definition to change (which would reset resize state).
    const hiddenColumnEntriesRef = useRef([]);
    useEffect(() => {
        hiddenColumnEntriesRef.current = hiddenColumnEntries;
    }, [hiddenColumnEntries]);

    const showColumn = useCallback((columnId) => {
        if (!columnId) return;
        setHiddenColumnsState((prev) => prev.filter((id) => id !== columnId));
    }, []);

    const getHiddenEntries = useCallback(() => hiddenColumnEntriesRef.current || [], []);

    const RestoreColumnHeader = useCallback(
        () => <HiddenColumnsRestoreHeader getHiddenEntries={getHiddenEntries} onShowColumn={showColumn} />,
        [getHiddenEntries, showColumn]
    );

    const columnsWithRestore = useMemo(() => {
        let hasRestore = false;
        const restoreConfig = createRestoreColumnConfig(RestoreColumnHeader);

        const processColumns = (cols = []) =>
            cols.map((col) => {
                if (col?.columns?.length) {
                    return { ...col, columns: processColumns(col.columns) };
                }
                if ((col?.id || col?.accessor) === RESTORE_COLUMN_ID) {
                    hasRestore = true;
                    return { ...col, ...restoreConfig, Cell: col.Cell || restoreConfig.Cell };
                }
                return col;
            });

        const processed = processColumns(columns || []);
        return hasRestore ? processed : [...processed, restoreConfig];
    }, [RestoreColumnHeader, columns]);

    // backwards compatibility with older templates
    const columnOrderMeta = useMemo(() => {
        const leafIds = [];
        const stickyLeftIds = new Set();
        const stickyRightIds = new Set();

        const walk = (cols = [], parent = null) => {
            cols.forEach((col) => {
                if (col?.columns?.length) {
                    walk(col.columns, col);
                    return;
                }

                const id = col?.id || col?.accessor;
                if (!id) return;
                leafIds.push(id);

                const isLeft = col?.sticky === 'left' || parent?.sticky === 'left';
                const isRight = col?.sticky === 'right' || parent?.sticky === 'right' || parent?.id === 'sticky-right';

                if (isLeft) stickyLeftIds.add(id);
                if (isRight) stickyRightIds.add(id);
            });
        };

        walk(columnsWithRestore);
        return { leafIds, stickyLeftIds, stickyRightIds };
    }, [columnsWithRestore]);

    const normalizedInitialState = useMemo(() => {
        if (!initialStateProp) return initialStateProp;
        const nextState = { ...initialStateProp };
        const savedOrder = Array.isArray(initialStateProp.columnOrder) ? initialStateProp.columnOrder : [];
        if (!savedOrder.length) return nextState;

        const { leafIds, stickyLeftIds, stickyRightIds } = columnOrderMeta;
        const leafIdSet = new Set(leafIds);
        const isReorderable = (id) =>
            leafIdSet.has(id) && !stickyLeftIds.has(id) && !stickyRightIds.has(id) && !NON_HIDEABLE_COLUMNS.has(id);

        const defaultReorderable = leafIds.filter(isReorderable);
        const savedReorderable = savedOrder.filter(isReorderable);
        const savedSet = new Set(savedReorderable);
        const mergedReorderable = [...savedReorderable, ...defaultReorderable.filter((id) => !savedSet.has(id))];

        const leftIds = leafIds.filter((id) => stickyLeftIds.has(id));
        const rightIds = leafIds.filter((id) => stickyRightIds.has(id) || NON_HIDEABLE_COLUMNS.has(id));

        nextState.columnOrder = [...leftIds, ...mergedReorderable, ...rightIds];
        return nextState;
    }, [initialStateProp, columnOrderMeta]);

    const normalizedSortBy = useMemo(() => (Array.isArray(sortBy) ? sortBy : sortBy ? [sortBy] : []), [sortBy]);

    const tableConfig = useMemo(() => {
        const config = {
            columns: columnsWithRestore,
            data: tableData,
            initialState: {
                sortBy: normalizedSortBy.length ? normalizedSortBy : [],
                ...(normalizedInitialState || {}),
            },
            manualSortBy,
            getRowId: (row, index, parent) => {
                if (typeof getRowId === 'function') return getRowId(row, index, parent);
                return row?.order_id || row?.id || String(index);
            },
            autoResetHiddenColumns: false,
            autoResetResize: false,
            autoResetColumnOrder: false,
            defaultColumn,
        };

        if (enableRowSelection) {
            config.initialState.selectedRowIds = selectedRowIds;
            config.enableRowSelection = true;
            config.enableMultiRowSelection = true;
            config.autoResetSelectedRows = true;
        }

        return config;
    }, [
        columnsWithRestore,
        tableData,
        normalizedSortBy,
        enableRowSelection,
        selectedRowIds,
        defaultColumn,
        initialStateProp,
        getRowId,
        manualSortBy,
    ]);

    const tableHooks = useMemo(() => {
        const hooks = [useSortBy];

        if (enableColumnResizing) {
            hooks.push(useResizeColumns);
        }

        if (enableColumnReordering) {
            hooks.push(useColumnOrder);
        }

        hooks.push(useFlexLayout);

        if (enableRowSelection) {
            hooks.push(useRowSelect);
        }

        hooks.push(useSticky);

        return hooks;
    }, [enableRowSelection, enableColumnResizing, enableColumnReordering]);

    // Capture the initial "default" sort only once (used for "Clear Sorting")
    const defaultSortRef = useRef(null);
    if (!defaultSortRef.current) {
        const initial = normalizedSortBy?.[0];
        if (initial?.id) defaultSortRef.current = initial;
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        allColumns,
        setColumnOrder,
        setSortBy,
        setHiddenColumns: setTableHiddenColumns,
        state,
    } = useTable(tableConfig, ...tableHooks, (hooks) => {
        if (enableRowSelection) {
            const scale = 1.2;
            const selectionColWidth = Math.max(50, Math.round(50 * scale));
            const scaledCheckbox = (props) => (
                <span
                    css={css`
                        display: inline-flex;
                        transform: scale(${scale});
                        transform-origin: center;
                    `}
                >
                    <IndeterminateCheckbox {...props} />
                </span>
            );

            hooks.visibleColumns.push((columns) => [
                {
                    id: 'selection',
                    sticky: 'left',
                    width: selectionColWidth,
                    minWidth: selectionColWidth,
                    maxWidth: selectionColWidth,
                    Header: ({ getToggleAllRowsSelectedProps }) => {
                        const controller = selectionControllerRef.current;
                        if (controller) return <ControlledSelectionHeader controller={controller} />;
                        return scaledCheckbox(getToggleAllRowsSelectedProps());
                    },
                    Cell: ({ row }) => {
                        const controller = selectionControllerRef.current;
                        return (
                            <span
                                onClick={(e) => e.stopPropagation()}
                                css={css`
                                    display: inline-flex;
                                    justify-content: center;
                                    align-items: center;
                                `}
                            >
                                {controller ? (
                                    <ControlledSelectionCell controller={controller} rowId={row.id} />
                                ) : (
                                    scaledCheckbox(row.getToggleRowSelectedProps())
                                )}
                            </span>
                        );
                    },
                },
                ...columns,
            ]);
        }
        if (highlightRow) {
            hooks.prepareRow.push(highlightRow);
        }
    });

    useEffect(() => {
        setTableHiddenColumns(hiddenColumns);
    }, [hiddenColumns, setTableHiddenColumns]);

    useEffect(() => {
        if (normalizedSortBy.length) {
            setSortBy(normalizedSortBy);
        }
    }, [normalizedSortBy, setSortBy]);

    // Emit layout changes to parent (for saving profiles)
    useEffect(() => {
        if (!onLayoutChange) return;
        const payload = {
            hiddenColumns: state?.hiddenColumns || [],
            columnOrder: state?.columnOrder || [],
            columnWidths: state?.columnResizing?.columnWidths || {},
        };
        onLayoutChange(payload);
    }, [state?.hiddenColumns, state?.columnOrder, state?.columnResizing?.columnWidths, onLayoutChange]);

    const resetSortingToDefault = useCallback(() => {
        const def = defaultSortRef.current;
        if (def?.id) {
            setSortBy([def]);
        } else {
            setSortBy([]);
        }
    }, [setSortBy]);

    const hideColumn = useCallback(
        (columnId) => {
            if (!columnId || NON_HIDEABLE_COLUMNS.has(columnId)) return;

            // Clear sort if this column is currently sorted
            if (state?.sortBy?.[0]?.id === columnId) setSortBy([]);

            // Clear any active column-level filters
            onClearColumnFilter?.(columnId);

            setHiddenColumnsState((prev) => (prev.includes(columnId) ? prev : [...prev, columnId]));
        },
        [onClearColumnFilter, setSortBy, state?.sortBy]
    );

    const isRestoreColumn = useCallback((header) => header.id === RESTORE_COLUMN_ID, []);

    useEffect(() => {
        if (!onSortChange) return;
        if (state?.sortBy?.length) {
            onSortChange(state.sortBy[0]);
        } else {
            onSortChange(null);
        }
    }, [state?.sortBy, onSortChange]);

    useEffect(() => {
        if (selectionController) return;
        if (enableRowSelection && state?.selectedRowIds && onSelectionChange) {
            onSelectionChange(state.selectedRowIds);
        }
    }, [state?.selectedRowIds, enableRowSelection, onSelectionChange, selectionController]);

    const isStickyLeft = useCallback((header) => {
        const col = header.column || header;
        return (
            header.id === 'selection' ||
            header.sticky === 'left' ||
            col.sticky === 'left' ||
            header.parent?.sticky === 'left'
        );
    }, []);

    const isStickyRight = useCallback((header) => {
        const col = header.column || header;
        return (
            header.sticky === 'right' ||
            col.sticky === 'right' ||
            header.parent?.sticky === 'right' ||
            header.parent?.id === 'sticky-right'
        );
    }, []);

    // Build a stable list of leaf header columns for drag-and-drop
    const headerLeafColumns = useMemo(() => {
        if (!headerGroups?.length) return [];
        const [group] = headerGroups;
        const leafs = [];

        const collectLeafs = (header) => {
            if (header.isVisible === false) return;
            if (header.headers && header.headers.length) {
                header.headers.forEach(collectLeafs);
            } else {
                leafs.push(header);
            }
        };

        group.headers.forEach(collectLeafs);
        return leafs;
    }, [headerGroups]);

    const lockedLeftColumns = useMemo(
        () => headerLeafColumns.filter((col) => isStickyLeft(col) && !isRestoreColumn(col)),
        [headerLeafColumns, isStickyLeft, isRestoreColumn]
    );

    const lockedRightColumns = useMemo(
        () => headerLeafColumns.filter((col) => isStickyRight(col)),
        [headerLeafColumns, isStickyRight]
    );

    const reorderableColumns = useMemo(
        () => headerLeafColumns.filter((col) => !isStickyLeft(col) && !isStickyRight(col) && !isRestoreColumn(col)),
        [headerLeafColumns, isStickyLeft, isStickyRight, isRestoreColumn]
    );

    const onColumnDragEnd = useCallback(
        (result) => {
            if (!result.destination || result.source.index === result.destination.index) return;

            const reorderedIds = Array.from(reorderableColumns.map((col) => col.id));
            const [movedId] = reorderedIds.splice(result.source.index, 1);
            reorderedIds.splice(result.destination.index, 0, movedId);

            const leftIds = lockedLeftColumns.map((col) => col.id);
            const rightIds = lockedRightColumns.map((col) => col.id);
            const allIds = new Set([...leftIds, ...reorderedIds, ...rightIds]);
            const remainingIds = headerLeafColumns.map((col) => col.id).filter((id) => !allIds.has(id));

            setColumnOrder([...leftIds, ...reorderedIds, ...rightIds, ...remainingIds]);
        },
        [lockedLeftColumns, lockedRightColumns, reorderableColumns, setColumnOrder, headerLeafColumns]
    );

    const headerRowRender = () => {
        const [group] = headerGroups;

        const headerContent = (
            <div
                {...group.getHeaderGroupProps({
                    style: {
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        display: 'flex',
                        flexBasis: 'auto',
                        flexGrow: 0,
                        flexShrink: 1,
                        height: 40, // Match body row height
                    },
                })}
            >
                {/* Locked left columns (e.g. selection checkbox) */}
                {lockedLeftColumns.map((col) => (
                    <HeaderCell
                        {...col.getHeaderProps({
                            style: {
                                backgroundColor: 'white',
                            },
                        })}
                        key={col.id}
                        cell={col}
                        align={col.align}
                        enableResizing={enableColumnResizing}
                        onClearSorting={resetSortingToDefault}
                        isColumnFilterable={isColumnFilterable}
                        onOpenFilter={onOpenColumnFilter}
                    />
                ))}

                {/* Reorderable middle columns */}
                {enableColumnReordering ? (
                    <DragDropContext onDragEnd={onColumnDragEnd}>
                        <Droppable droppableId="orders-table-columns" direction="horizontal">
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    css={css`
                                        display: flex;
                                    `}
                                >
                                    {reorderableColumns.map((col, index) => (
                                        <Draggable draggableId={col.id} index={index} key={col.id}>
                                            {(dragProvided) => {
                                                // Let react-table control width/flex via headerProps.style
                                                const headerProps = col.getHeaderProps();
                                                const { style: headerStyle, key, ...restHeaderProps } = headerProps;

                                                return (
                                                    <div
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        style={{
                                                            ...(headerStyle || {}),
                                                            ...(dragProvided.draggableProps.style || {}),
                                                        }}
                                                    >
                                                        <HeaderCell
                                                            {...restHeaderProps}
                                                            cell={col}
                                                            align={col.align}
                                                            enableResizing={enableColumnResizing}
                                                            // Only the inner header content is a drag handle;
                                                            // the resize handle remains resize-only.
                                                            dragHandleProps={dragProvided.dragHandleProps}
                                                            onClearSorting={resetSortingToDefault}
                                                            isColumnFilterable={isColumnFilterable}
                                                            onOpenFilter={onOpenColumnFilter}
                                                            onHideColumn={hideColumn}
                                                        />
                                                    </div>
                                                );
                                            }}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : (
                    reorderableColumns.map((col) => (
                        <HeaderCell
                            {...col.getHeaderProps({
                                style: {
                                    backgroundColor: 'white',
                                },
                            })}
                            key={col.id}
                            cell={col}
                            align={col.align}
                            enableResizing={enableColumnResizing}
                            onClearSorting={resetSortingToDefault}
                            isColumnFilterable={isColumnFilterable}
                            onOpenFilter={onOpenColumnFilter}
                            onHideColumn={hideColumn}
                        />
                    ))
                )}

                {/* Locked right columns (e.g. sticky right status) */}
                {lockedRightColumns.map((col) => {
                    const isRestore = isRestoreColumn(col);
                    return (
                        <HeaderCell
                            {...col.getHeaderProps({
                                style: {
                                    backgroundColor: 'white',
                                },
                            })}
                            key={col.id}
                            cell={col}
                            align={col.align || 'center'}
                            enableResizing={!isRestore && enableColumnResizing}
                            onClearSorting={resetSortingToDefault}
                            isColumnFilterable={isColumnFilterable}
                            onOpenFilter={onOpenColumnFilter}
                            onHideColumn={hideColumn}
                            disableColumnMenu={isRestore}
                        />
                    );
                })}
            </div>
        );

        return headerContent;
    };

    const rowsToRender = useMemo(() => {
        if (virtualizeRows) return rows;
        return rows.map((row) => {
            prepareRow(row);
            return row;
        });
    }, [rows, prepareRow, virtualizeRows]);

    const containerRef = useRef(null);

    const [visibleRange, setVisibleRange] = useState({ start: 0, end: VIRTUAL_OVERSCAN * 4 });

    const scrollStateRef = useRef({});
    scrollStateRef.current = {
        rowCount: rowsToRender.length,
        hasMore,
        isLoading,
        onLoadMore,
        loadMoreThreshold,
        virtualizeRows,
    };

    const recomputeVisibleRange = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const { rowCount } = scrollStateRef.current;
        const first = Math.max(0, Math.floor(el.scrollTop / VIRTUAL_ROW_HEIGHT) - VIRTUAL_OVERSCAN);
        const visibleCount = Math.ceil(el.clientHeight / VIRTUAL_ROW_HEIGHT) + VIRTUAL_OVERSCAN * 2;
        const last = Math.min(rowCount, first + visibleCount);
        setVisibleRange((prev) => (prev.start === first && prev.end === last ? prev : { start: first, end: last }));
    }, []);

    useEffect(() => {
        if (virtualizeRows) recomputeVisibleRange();
    }, [virtualizeRows, rowsToRender.length, recomputeVisibleRange]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        if (!onLoadMore && !virtualizeRows) return;

        const handleScroll = () => {
            const s = scrollStateRef.current;
            if (s.virtualizeRows) recomputeVisibleRange();
            if (s.onLoadMore && !s.isLoading && s.hasMore) {
                const { scrollTop, scrollHeight, clientHeight } = el;
                if (scrollHeight - scrollTop - clientHeight < s.loadMoreThreshold) s.onLoadMore();
            }
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [onLoadMore, virtualizeRows, recomputeVisibleRange]);

    const renderRow = (row, index) => {
        const [color, hoverColor] =
            callbacks?.handleColor?.({
                order: row.original,
                index,
                userSettings: milestoneThresholdSettings,
                view,
            }) || [];
        const resolvedCellRenderContext =
            typeof cellRenderContext === 'function'
                ? cellRenderContext({ row: row.original, index })
                : cellRenderContext ?? { callbacks };
        return (
            <TableRow
                key={row.id}
                {...row.getRowProps({})}
                row={row}
                style={{ height: 40, width: '100%' }}
                onClick={(e) => onRowClick?.(row.original, e)}
                error={row.red}
                color={hoverColor}
                useDiv={useDiv}
            >
                {row.cells.map((cell) => (
                    <BodyCell
                        key={cell.column.id}
                        {...cell.getCellProps({
                            style: {
                                height: 40,
                            },
                        })}
                        cell={cell}
                        align={cell.column.align}
                        index={index}
                        callbacks={callbacks}
                        color={color}
                        renderContext={resolvedCellRenderContext}
                        wrapContent={wrapBodyCellContent}
                    />
                ))}
            </TableRow>
        );
    };

    const renderBodyRows = () => {
        if (!virtualizeRows) {
            return rowsToRender.map((row, index) => renderRow(row, index));
        }
        const total = rowsToRender.length;
        const start = Math.min(visibleRange.start, total);
        const end = Math.min(visibleRange.end, total);
        return (
            <>
                {start > 0 && <div style={{ height: start * VIRTUAL_ROW_HEIGHT, flexShrink: 0 }} />}
                {rowsToRender.slice(start, end).map((row, i) => {
                    prepareRow(row);
                    return renderRow(row, start + i);
                })}
                {end < total && <div style={{ height: (total - end) * VIRTUAL_ROW_HEIGHT, flexShrink: 0 }} />}
            </>
        );
    };

    return (
        <TableContainer
            ref={containerRef}
            css={css`
                flex: 1 1 0;
                min-height: 0;
                min-width: 0;
                width: 100%;
                height: 100%;
                background-color: white;
                overflow: auto;
            `}
        >
            <div
                {...getTableProps()}
                css={css`
                    height: 100%;
                    display: flex;
                    flex-grow: 1;
                    flex-direction: column;
                `}
            >
                <div
                    {...getTableBodyProps()}
                    css={css`
                        display: flex;
                        flex-grow: 1;
                        flex-direction: column;
                    `}
                >
                    {headerRowRender()}
                    {renderBodyRows()}
                </div>
            </div>
        </TableContainer>
    );
};

export default CustomizableTable;
